import axios from 'axios';
import { ANALYSIS_BANK, PORTFOLIO_ANALYSIS } from './analysisBank';
import API from './api';

const CEREBRO_API = axios.create({
  baseURL: import.meta.env.VITE_INVESTMENTS_API_URL || 'http://localhost:5001',
  headers: { 'Content-Type': 'application/json' },
  timeout: 3000, // Quick timeout to fall back to offline mode
});

CEREBRO_API.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

/**
 * Ejecuta una orden de acciones VIA BACKEND (no directo a Alpaca).
 * El backend calcula y cobra la comision escalonada + IVA (Fireblocks),
 * registra el Trade y factura via Siigo. Timeout largo (Fireblocks + Alpaca).
 */
export async function placeStockOrderBackend({ symbol, notional, side = 'buy' }) {
  const { data } = await CEREBRO_API.post(
    '/api/orders/stock',
    { symbol, notional, side },
    { timeout: 30000 }
  );
  return data;
}

// ==================== OFFLINE FALLBACK ====================

let backendAvailable = null; // null = not checked, true/false = result
let backendCheckPromise = null;

async function checkBackend() {
  if (backendAvailable !== null) return backendAvailable;
  if (backendCheckPromise) return backendCheckPromise;

  backendCheckPromise = (async () => {
    try {
      await CEREBRO_API.get('/health', { timeout: 2000 });
      backendAvailable = true;
    } catch {
      backendAvailable = false;
      console.info('[Cerebro] Backend unavailable, using offline analysis bank');
    }
    return backendAvailable;
  })();

  return backendCheckPromise;
}

function getOfflineStockAnalysis(symbol, tier = 'basic') {
  const data = ANALYSIS_BANK[symbol.toUpperCase()];
  if (!data || !data[tier]) {
    return {
      symbol: symbol.toUpperCase(),
      tier,
      analysis: `Analisis para ${symbol.toUpperCase()} aun no disponible. El Cerebro lo agregara pronto.`,
      qualitative: null,
      fundamentals: null,
      technicals: null,
      source: 'unavailable',
      date: new Date().toISOString().split('T')[0],
    };
  }
  return {
    symbol: symbol.toUpperCase(),
    tier,
    analysis: data[tier],
    qualitative: tier === 'advanced' || tier === 'premium' ? data.qualitative : null,
    fundamentals: data.fundamentals || null,
    technicals: null,
    scenarios: ['advanced', 'premium'].includes(tier) ? (data.scenarios || null) : null,
    source: 'offline',
    date: new Date().toISOString().split('T')[0],
  };
}

function getOfflinePortfolioAnalysis(portfolioType, tier = 'basic') {
  const data = PORTFOLIO_ANALYSIS[portfolioType];
  if (!data || !data[tier]) {
    return {
      portfolioType,
      tier,
      analysis: 'Analisis del portafolio en preparacion.',
      source: 'unavailable',
    };
  }
  return {
    portfolioType,
    tier,
    analysis: data[tier],
    source: 'offline',
  };
}

// ==================== ANALYSIS ====================

export async function getStockAnalysis(symbol, tier = null) {
  const isUp = await checkBackend();
  const effectiveTier = tier || getEffectiveTier();

  if (!isUp) {
    return getOfflineStockAnalysis(symbol, effectiveTier);
  }

  try {
    const params = tier ? { tier } : {};
    const { data } = await CEREBRO_API.get(`/api/analysis/stock/${symbol}`, { params });
    return data.data;
  } catch {
    return getOfflineStockAnalysis(symbol, effectiveTier);
  }
}

/**
 * Returns analysis for ALL tiers in one call (for the tier-tabs UI).
 * Falls back to assembling all tiers from the offline bank when backend is down.
 */
export async function getStockAnalysisAllTiers(symbol) {
  const isUp = await checkBackend();

  if (isUp) {
    try {
      const { data } = await CEREBRO_API.get(`/api/analysis/stock/${symbol}`, {
        params: { tier: 'all' },
      });
      return data.data;
    } catch {
      // fall through to offline
    }
  }

  const upper = symbol.toUpperCase();
  const bank = ANALYSIS_BANK[upper];
  const tiers = { basic: null, intermediate: null, advanced: null, premium: null };
  if (bank) {
    for (const t of Object.keys(tiers)) tiers[t] = bank[t] || null;
  }
  return {
    symbol: upper,
    date: new Date().toISOString().split('T')[0],
    source: bank ? 'offline' : 'pending',
    tiers,
    qualitative: bank?.qualitative || null,
    fundamentals: bank?.fundamentals || null,
    technicals: null,
    userTier: getEffectiveTier(),
  };
}

export async function getPortfolioAnalysis(tier = null) {
  const isUp = await checkBackend();

  if (!isUp) {
    // Get portfolio from local profile
    const profileStr = localStorage.getItem('fp_investor_profile');
    const profile = profileStr ? JSON.parse(profileStr) : null;
    const portfolioType = profile?.portfolio || 'explorer';
    const effectiveTier = tier || getEffectiveTier();
    const result = getOfflinePortfolioAnalysis(portfolioType, effectiveTier);
    return {
      ...result,
      portfolio: portfolioType,
      thesis: profile?.thesis || '',
      userTier: effectiveTier,
    };
  }

  try {
    const params = tier ? { tier } : {};
    const { data } = await CEREBRO_API.get('/api/analysis/portfolio', { params });
    return data.data;
  } catch {
    const profileStr = localStorage.getItem('fp_investor_profile');
    const profile = profileStr ? JSON.parse(profileStr) : null;
    const portfolioType = profile?.portfolio || 'explorer';
    const effectiveTier = tier || getEffectiveTier();
    return {
      ...getOfflinePortfolioAnalysis(portfolioType, effectiveTier),
      portfolio: portfolioType,
      thesis: profile?.thesis || '',
      userTier: effectiveTier,
    };
  }
}

export async function getAvailableSymbols() {
  // Fuente de verdad: el banco del backend (incluye las acciones publicadas, p.ej. SPCX, CAI, RXRX...).
  try {
    const { data } = await CEREBRO_API.get('/api/analysis/symbols', { timeout: 4000 });
    if (Array.isArray(data?.data) && data.data.length) return data.data;
  } catch { /* offline / backend caido: cae al banco local */ }
  return Object.keys(ANALYSIS_BANK);
}

export async function getSnapshot(symbol) {
  const isUp = await checkBackend();
  if (!isUp) return null;
  try {
    const { data } = await CEREBRO_API.get(`/api/analysis/snapshot/${symbol}`);
    return data.data;
  } catch {
    return null;
  }
}

export async function getTechnicals(symbol) {
  const isUp = await checkBackend();
  if (!isUp) return null;
  try {
    const { data } = await CEREBRO_API.get(`/api/analysis/technicals/${symbol}`);
    return data.data;
  } catch {
    return null;
  }
}

// ==================== INVESTOR ====================

export async function completeOnboarding(answers) {
  const isUp = await checkBackend();

  if (!isUp) {
    // Calculate locally and save to localStorage
    const portfolio = calculateLocalPortfolio(answers);
    const thesis = generateLocalThesis(portfolio);
    const profile = {
      answers,
      portfolio,
      thesis,
      tier: 'basic',
      level: 1,
      createdAt: new Date().toISOString(),
    };
    localStorage.setItem('fp_investor_profile', JSON.stringify(profile));
    return profile;
  }

  try {
    const { data } = await CEREBRO_API.post('/api/investor/onboarding', { answers });
    localStorage.setItem('fp_investor_profile', JSON.stringify({ ...data.data, answers }));
    return data.data;
  } catch {
    const portfolio = calculateLocalPortfolio(answers);
    const thesis = generateLocalThesis(portfolio);
    const profile = { answers, portfolio, thesis, tier: 'basic', level: 1, createdAt: new Date().toISOString() };
    localStorage.setItem('fp_investor_profile', JSON.stringify(profile));
    return profile;
  }
}

/**
 * Creates an Alpaca brokerage account for the authenticated user.
 * Called right after onboarding completes (auto-provision).
 * Returns { alpacaAccountId, status, funded } on success, null on failure.
 */
export async function createAlpacaAccount({ firstName, lastName, phoneNumber, initialFundUsd = 5000 }) {
  const isUp = await checkBackend();
  if (!isUp) return null;
  try {
    const { data } = await CEREBRO_API.post('/api/investor/create-alpaca-account', {
      firstName,
      lastName,
      phoneNumber,
      initialFundUsd,
    });
    return data.data;
  } catch (err) {
    const msg = err?.response?.data?.message || err.message;
    console.error('[Cerebro] createAlpacaAccount failed:', msg);
    return null;
  }
}

/**
 * Idempotente: garantiza que el usuario del beta tenga su cuenta Alpaca + saldo de
 * practica. Barato si ya existe (solo la devuelve). Seguro de llamar al cargar
 * inversiones o al terminar onboarding.
 * Devuelve { ok, alpacaAccountId?, created?, funded?, provisioning?, code?, message? }.
 */
export async function ensureAlpacaAccount() {
  const isUp = await checkBackend();
  if (!isUp) return { ok: false, code: 'BACKEND_DOWN' };
  try {
    const { data } = await CEREBRO_API.post('/api/investor/ensure-alpaca-account', {});
    return { ok: true, ...(data.data || {}) };
  } catch (err) {
    const code = err?.response?.data?.code || 'ERROR';
    const message = err?.response?.data?.message || err.message;
    return { ok: false, code, message };
  }
}

export async function getInvestorProfile() {
  const isUp = await checkBackend();

  if (!isUp) {
    const profileStr = localStorage.getItem('fp_investor_profile');
    if (profileStr) return JSON.parse(profileStr);
    throw new Error('No profile found');
  }

  try {
    const { data } = await CEREBRO_API.get('/api/investor/profile');
    return data.data;
  } catch {
    const profileStr = localStorage.getItem('fp_investor_profile');
    if (profileStr) return JSON.parse(profileStr);
    throw new Error('No profile found');
  }
}

export async function getLevel() {
  const isUp = await checkBackend();

  if (!isUp) {
    const stored = localStorage.getItem('fp_user_level');
    if (stored) return JSON.parse(stored);
    return {
      level: 1,
      achievements: [],
      lessonsCompleted: [],
      totalInvested: 0,
      stocksViewed: [],
    };
  }

  try {
    const { data } = await CEREBRO_API.get('/api/investor/level');
    return data.data;
  } catch {
    return { level: 1, achievements: [], lessonsCompleted: [], totalInvested: 0, stocksViewed: [] };
  }
}

export async function completeLesson(lessonId) {
  const isUp = await checkBackend();
  if (!isUp) {
    const stored = localStorage.getItem('fp_user_level');
    const level = stored ? JSON.parse(stored) : { lessonsCompleted: [] };
    if (!level.lessonsCompleted.includes(lessonId)) {
      level.lessonsCompleted.push(lessonId);
    }
    localStorage.setItem('fp_user_level', JSON.stringify(level));
    return level;
  }
  try {
    const { data } = await CEREBRO_API.post(`/api/investor/lesson/${lessonId}`);
    return data.data;
  } catch {
    return { lessonsCompleted: [lessonId], newAchievements: [] };
  }
}

export async function updateTier(equity) {
  const isUp = await checkBackend();
  if (!isUp) return { tier: 'basic', equity };
  try {
    const { data } = await CEREBRO_API.post('/api/investor/update-tier', { equity });
    return data.data;
  } catch {
    return { tier: 'basic', equity };
  }
}

export async function checkLevelUp() {
  const isUp = await checkBackend();
  if (!isUp) return { leveledUp: false };
  try {
    const { data } = await CEREBRO_API.post('/api/investor/check-levelup');
    return data.data;
  } catch {
    return { leveledUp: false };
  }
}

// ==================== PILOTO AUTOMATICO (DCA) ====================

export async function createRecurringPlan({ symbol, notional, frequency, dayOfWeek, dayOfMonth, nickname }) {
  try {
    const { data } = await CEREBRO_API.post('/api/investor/recurring', {
      symbol, notional, frequency, dayOfWeek, dayOfMonth, nickname,
    });
    return data;
  } catch (err) {
    const msg = err?.response?.data?.message || 'Error creando plan recurrente.';
    return { success: false, message: msg, existingPlanId: err?.response?.data?.existingPlanId };
  }
}

export async function listRecurringPlans(status) {
  try {
    const params = status ? { status } : {};
    const { data } = await CEREBRO_API.get('/api/investor/recurring', { params });
    return data;
  } catch {
    return { success: false, data: [] };
  }
}

export async function getRecurringPlan(id) {
  try {
    const { data } = await CEREBRO_API.get(`/api/investor/recurring/${id}`);
    return data;
  } catch {
    return { success: false, data: null };
  }
}

export async function updateRecurringPlan(id, updates) {
  try {
    const { data } = await CEREBRO_API.patch(`/api/investor/recurring/${id}`, updates);
    return data;
  } catch (err) {
    return { success: false, message: err?.response?.data?.message || 'Error actualizando plan.' };
  }
}

export async function pauseRecurringPlan(id) {
  try {
    const { data } = await CEREBRO_API.post(`/api/investor/recurring/${id}/pause`);
    return data;
  } catch (err) {
    return { success: false, message: err?.response?.data?.message || 'Error pausando plan.' };
  }
}

export async function resumeRecurringPlan(id) {
  try {
    const { data } = await CEREBRO_API.post(`/api/investor/recurring/${id}/resume`);
    return data;
  } catch (err) {
    return { success: false, message: err?.response?.data?.message || 'Error reanudando plan.' };
  }
}

export async function cancelRecurringPlan(id) {
  try {
    const { data } = await CEREBRO_API.delete(`/api/investor/recurring/${id}`);
    return data;
  } catch (err) {
    return { success: false, message: err?.response?.data?.message || 'Error cancelando plan.' };
  }
}

// ==================== REFERIDOS (Invita y Gana) ====================
// Nota: estos endpoints viven en el backend principal (VITE_API_URL),
// no en investments-api. Usamos el axios instance de api.js.

export async function getReferralStats() {
  try {
    const { data } = await API.get('/referral/stats');
    return data;
  } catch (err) {
    console.error('[Referral] getReferralStats error:', err?.response?.data?.message || err.message);
    return { success: false, data: null };
  }
}

export async function getReferralCode() {
  try {
    const { data } = await API.get('/referral/code');
    return data;
  } catch (err) {
    console.error('[Referral] getReferralCode error:', err?.response?.data?.message || err.message);
    return { success: false, data: null };
  }
}

// ==================== PORTFOLIO LIVE ====================

/**
 * Fetches live portfolio status: positions, deviations, health score, rebalance suggestions.
 * Response depth depends on user tier (basic → premium).
 * Returns null if backend is down or user has no profile.
 */
export async function getPortfolioLive() {
  const isUp = await checkBackend();
  if (!isUp) {
    console.warn('[PortfolioLive] Backend not available');
    return null;
  }
  try {
    const { data } = await CEREBRO_API.get('/api/analysis/portfolio-live');
    return data.data;
  } catch (err) {
    console.error('[PortfolioLive] API error:', err?.response?.status, err?.response?.data || err.message);
    return null;
  }
}

// ==================== HEALTH ====================

export async function checkHealth() {
  try {
    const { data } = await CEREBRO_API.get('/health');
    return data;
  } catch {
    return { status: 'offline', mode: 'frontend-only' };
  }
}

// ==================== DEMO MODE ====================

const DEMO_KEY = 'fp_demo_tier';

export function getDemoTier() {
  return localStorage.getItem(DEMO_KEY) || null;
}

export function setDemoTier(tier) {
  if (tier) localStorage.setItem(DEMO_KEY, tier);
  else localStorage.removeItem(DEMO_KEY);
}

export function toggleDemoTier() {
  // Cycle: null (real) → basic → intermediate → advanced → premium → null
  const current = getDemoTier();
  const cycle = [null, 'basic', 'intermediate', 'advanced', 'premium'];
  const currentIndex = cycle.indexOf(current);
  const nextIndex = (currentIndex + 1) % cycle.length;
  const nextTier = cycle[nextIndex];
  setDemoTier(nextTier);
  return nextTier;
}

export function getEffectiveTier() {
  const demo = getDemoTier();
  if (demo) return demo;
  const profileStr = localStorage.getItem('fp_investor_profile');
  if (profileStr) {
    try {
      return JSON.parse(profileStr).tier || 'basic';
    } catch {}
  }
  return 'basic';
}

// Demo accounts (Progresion, founders) — always full access for demos
const FULL_ACCESS_EMAILS = [
  'ivan@flowexpay.com', 'ivanauli79@gmail.com', 'ivansarmi@hotmail.com',
  'juanlamboglia@icloud.com', 'dr.ioanisvalencia@gmail.com', 'jasarmientosa@hotmail.com',
];

/**
 * Puede el usuario EJECUTAR trades por voz con el Oraculo?
 * Solo Premium (tier $50K+) o cuentas demo. Los demas pueden consultar pero no ejecutar.
 * @param {string} email - email del usuario (del store valtio)
 */
export function canExecuteWithOraculo(email) {
  if (email && FULL_ACCESS_EMAILS.includes(email.toLowerCase())) return true;
  return getEffectiveTier() === 'premium';
}

// ==================== LOCAL HELPERS ====================

function calculateLocalPortfolio(answers) {
  if (answers.reactionToDrop === 'sell' || (answers.timeHorizon === 'short' && answers.excitement === 'safe_growth')) {
    return 'guardian';
  }
  if (answers.excitement === 'passive_income') {
    return 'income_builder';
  }
  if (answers.reactionToDrop === 'buy_more' && (answers.timeHorizon === 'long' || answers.timeHorizon === 'unlimited') && answers.excitement === 'world_changers') {
    return 'visionary';
  }
  return 'explorer';
}

function generateLocalThesis(portfolio) {
  const theses = {
    guardian: 'Tu portafolio Guardian esta disenado para proteger tu capital mientras crece de forma estable. Priorizamos ETFs diversificados, bonos y activos defensivos. Tu perfil indica que prefieres dormir tranquilo a perseguir retornos agresivos — y eso es inteligente.',
    explorer: 'Tu portafolio Explorador combina crecimiento con proteccion. Tienes exposicion al mercado americano, mercados emergentes y activos defensivos. Es un balance para alguien que quiere crecer pero sin montarse en una montana rusa.',
    visionary: 'Tu portafolio Visionario apuesta por las empresas que estan cambiando el mundo. Sabes que la volatilidad es temporal y que las mejores empresas se ven "caras" en todo momento. Tu horizonte largo te da una ventaja que la mayoria no tiene: paciencia.',
    income_builder: 'Tu portafolio Constructor de Ingresos esta disenado para generar dividendos crecientes ano tras ano. En lugar de perseguir subidas de precio, estas construyendo una maquina de ingresos pasivos que trabaja para ti 24/7.',
  };
  return theses[portfolio] || theses.explorer;
}

// ==================== ORACULO CHAT (Agentic Trading Fase 1) ====================

/**
 * Envia un mensaje al Oraculo conversacional y recibe respuesta IA con datos reales.
 * @param {string} message - Mensaje del usuario
 * @param {Array} history - Historial [{role: 'user'|'assistant', content: string}]
 * @returns {Promise<{response: string, toolsUsed: string[]}>}
 */
// Oraculo Business — copiloto de tesoreria del dashboard empresarial (/business)
// Caja Total (bancos): extractos leídos con IA. NUNCA pedimos credenciales bancarias.
export async function getCajaTotal() {
  const { data } = await CEREBRO_API.get('/api/business-bancos/caja-total', { timeout: 12000 });
  return data.data;
}
export async function leerExtractoBancario(fileBase64, mediaType, texto) {
  const { data } = await CEREBRO_API.post('/api/business-bancos/extracto',
    { file_base64: fileBase64, media_type: mediaType, texto }, { timeout: 120000 }); // Claude leyendo el PDF
  return data.data;
}
export async function confirmarExtractoBancario(draft, fileBase64, mediaType, filename) {
  const { data } = await CEREBRO_API.post('/api/business-bancos/extracto/confirmar',
    { draft, file_base64: fileBase64, media_type: mediaType, filename }, { timeout: 30000 });
  return data.data;
}

// Cartera (COBRAR): links de cobro + métricas. Ningún endpoint mueve dinero.
// ---- Contratistas: destinos blockchain (la direccion la registra el titular) ----
export async function getFreelancers() {
  const { data } = await CEREBRO_API.get('/api/business-freelancers', { timeout: 12000 });
  return data.data;
}
export async function invitarFreelancer(payload) {
  const { data } = await CEREBRO_API.post('/api/business-freelancers/invitar', payload, { timeout: 20000 });
  return data.data;
}
// ENVIA la prueba de verdad (antes solo la marcaba). Timeout largo: es un
// envio real por blockchain.
export async function enviarPrueba(id) {
  // Devuelve { destino, link } — el link sirve para reenviarlo por WhatsApp.
  const { data } = await CEREBRO_API.post(`/api/business-freelancers/${id}/enviar-prueba`, {}, { timeout: 90000 });
  return data.data;
}
// Reenvia el enlace del titular SIN mover dinero (rota el token: el viejo muere).
// Para cuando la persona borro el correo o le cayo en spam.
export async function reenviarEnlaceFreelancer(id) {
  const { data } = await CEREBRO_API.post(`/api/business-freelancers/${id}/reenviar-enlace`, {}, { timeout: 20000 });
  return data.data;
}
export async function bloquearFreelancer(id, bloquear) {
  const { data } = await CEREBRO_API.post(`/api/business-freelancers/${id}/estado`, { bloquear }, { timeout: 12000 });
  return data.data;
}

// ---- Dispersion a contratistas ----
export async function getCandidatosDispersion() {
  const { data } = await CEREBRO_API.get('/api/business-dispersion/candidatos', { timeout: 12000 });
  return data.data;
}
export async function getDispersiones() {
  const { data } = await CEREBRO_API.get('/api/business-dispersion', { timeout: 12000 });
  return data.data;
}
export async function previsualizarPlantilla(texto) {
  const { data } = await CEREBRO_API.post('/api/business-dispersion/previsualizar', { texto }, { timeout: 20000 });
  return data.data;
}
export async function crearDispersion(payload) {
  const { data } = await CEREBRO_API.post('/api/business-dispersion', payload, { timeout: 15000 });
  return data.data;
}
// Sin telefono a proposito: el servidor manda el codigo al celular REGISTRADO
// del usuario autenticado. Si el numero viajara desde aqui, quien tenga la
// sesion abierta pondria el suyo y el SMS dejaria de ser un segundo factor.
// Devuelve { actionToken, telefonoFinal } — los ultimos 4 digitos, para mostrar.
export async function pedirCodigoDispersion(id) {
  const { data } = await CEREBRO_API.post(`/api/business-dispersion/${id}/codigo`, {}, { timeout: 20000 });
  return data.data;
}
// El backend arranca la dispersion y responde de UNA: no espera a terminar.
// Con 50 contratistas el envio secuencial pasa de 20 minutos y el navegador
// cortaria a los 3 — el dueno veria un error mientras la nomina sigue saliendo.
// Devuelve { lote, enCurso }; el progreso se consulta con getLoteDispersion.
export async function ejecutarDispersion(id, payload) {
  const { data } = await CEREBRO_API.post(`/api/business-dispersion/${id}/dispersar`, payload, { timeout: 30000 });
  return data.data;
}

/**
 * La empresa confirma que la prueba SI llego a esa direccion nueva: se envia lo
 * retenido y la direccion queda CONOCIDA — la proxima nomina va directa, sin
 * cuarentena. Sin esto el retenido se quedaba ahi para siempre: el backend ya
 * sabia liberarlo, pero la pantalla no tenia por donde pedirselo.
 */
export async function liberarRetenido(loteId, lineaId) {
  const { data } = await CEREBRO_API.post(
    `/api/business-dispersion/${loteId}/lineas/${lineaId}/liberar`, {}, { timeout: 60000 });
  return data.data;
}

export async function getLoteDispersion(id) {
  const { data } = await CEREBRO_API.get(`/api/business-dispersion/${id}`, { timeout: 15000 });
  return data.data;
}
export async function reintentarDispersion(id) {
  const { data } = await CEREBRO_API.post(`/api/business-dispersion/${id}/reintentar`, {}, { timeout: 30000 });
  return data.data;
}

// Billetera blockchain DE LA EMPRESA (direccion para recibir + saldos + permisos)
export async function getBlockchainBusiness() {
  const { data } = await CEREBRO_API.get('/api/business-blockchain', { timeout: 10000 });
  return data.data;
}

// Calendario de caja de la empresa (obligaciones + AP + cartera + festivos)
export async function getCalendarioBusiness(params = {}) {
  const { data } = await CEREBRO_API.get('/api/business-calendario', { params, timeout: 12000 });
  return data.data;
}
// Link para suscribir el calendario en Google Calendar / iPhone (?rotar=1 lo cambia)
export async function getSuscripcionCalendario(rotar = false) {
  const { data } = await CEREBRO_API.get('/api/business-calendario/suscripcion', {
    params: rotar ? { rotar: 1 } : {}, timeout: 10000,
  });
  return data.data;
}

export async function getCarteraBusiness() {
  const { data } = await CEREBRO_API.get('/api/business-cobros', { timeout: 10000 });
  return data.data;
}
export async function crearCobroBusiness(payload) {
  const { data } = await CEREBRO_API.post('/api/business-cobros', payload, { timeout: 10000 });
  return data.data;
}
export async function anularCobroBusiness(linkId) {
  const { data } = await CEREBRO_API.post('/api/business-cobros/anular', { linkId }, { timeout: 10000 });
  return data.data;
}

// Frente C: "Tu equipo de agentes" del Business — estado real per-empresa.
export async function getBusinessEquipo() {
  try {
    const { data } = await CEREBRO_API.get('/api/business-oraculo/equipo', { timeout: 8000 });
    return data.data?.agentes || [];
  } catch {
    return [];
  }
}

export async function oraculoBusinessChat(message, history = []) {
  try {
    const { data } = await CEREBRO_API.post('/api/business-oraculo/chat', { message, history }, { timeout: 30000 });
    return data.data || { response: 'Sin respuesta del Oraculo.' };
  } catch (err) {
    if (err?.response?.status === 429) return { response: 'Has enviado muchos mensajes. Espera un momento.' };
    if (err?.response?.status === 403) return { response: 'FlowexPay Business esta en piloto privado.' };
    return { response: 'No pude conectarme con el Oraculo de tu empresa. Intenta de nuevo.' };
  }
}

/**
 * Lo que se acaba de hablar por voz, para retomarlo al pasar al chat.
 * Nunca rompe la pantalla: si falla, devuelve vacio y el chat abre normal.
 */
/**
 * Un portafolio del catalogo (perfil + moneda), con sus dos patas.
 * El frontend NO tiene su propia copia de las composiciones: pide y pinta.
 */
/**
 * Quita los pesos quemados en la prosa del deep dive ("via VOO 35%" -> "via VOO").
 *
 * El backend ya la aplica; esto es el cinturon para cuando la respuesta sale del
 * banco OFFLINE, que no pasa por el servidor y trae los mismos numeros viejos.
 * Solo toca un porcentaje pegado a un ticker: "2-3% extra anual" o "~12% de
 * volatilidad" quedan intactos porque no son pesos de la cartera.
 * Gemela de sinPesosQuemados() en investments-api/data/portafolios.js.
 */
const PESO_PEGADO_A_TICKER = /\b([A-Z]{2,5})\s+~?\d{1,3}(?:[.,]\d+)?\s?%/g;

export function sinPesosQuemados(valor) {
  if (typeof valor === 'string') return valor.replace(PESO_PEGADO_A_TICKER, '$1');
  if (Array.isArray(valor)) return valor.map(sinPesosQuemados);
  if (valor && typeof valor === 'object') {
    return Object.fromEntries(Object.entries(valor).map(([k, v]) => [k, sinPesosQuemados(v)]));
  }
  return valor;
}

export async function getPortafolio(id) {
  try {
    const { data } = await CEREBRO_API.get(`/api/investor/portafolios/${encodeURIComponent(id)}`, { timeout: 10000 });
    return data?.data || null;
  } catch {
    return null;   // sin catalogo, la pantalla cae a la composicion de siempre
  }
}

export async function oraculoBusinessContextoVoz() {
  try {
    const { data } = await CEREBRO_API.get('/api/business-oraculo/contexto-voz', { timeout: 10000 });
    return data?.data?.mensajes || [];
  } catch {
    return [];
  }
}

// ===== Mateo Business — cuentas por pagar en lenguaje natural (/business/tesoreria) =====

/** Texto natural -> preview estructurado (NO guarda; el usuario confirma). */
export async function mateoCapture(text) {
  const { data } = await CEREBRO_API.post('/api/business-mateo/capture', { text }, { timeout: 30000 });
  return data.data;
}

/** Guarda las entradas confirmadas del preview. */
export async function mateoSaveEntries(entries, capturedFrom) {
  const { data } = await CEREBRO_API.post('/api/business-mateo/entries', { entries, capturedFrom }, { timeout: 15000 });
  return data.data;
}

/** Lista cuentas por pagar (estado opcional: por_pagar | pagada | anulada). */
export async function mateoListEntries(estado) {
  const { data } = await CEREBRO_API.get('/api/business-mateo/entries', { params: estado ? { estado } : {}, timeout: 15000 });
  return data.data;
}

/** Marca una cuenta como pagada (solo contabilidad, no mueve dinero). */
export async function mateoMarkPaid(entryId) {
  const { data } = await CEREBRO_API.post(`/api/business-mateo/entries/${entryId}/paid`, {}, { timeout: 15000 });
  return data.data;
}

/** Aging: vencidas / esta semana / este mes + totales por moneda. */
export async function mateoResumen() {
  const { data } = await CEREBRO_API.get('/api/business-mateo/resumen', { timeout: 15000 });
  return data.data;
}

// ===== Sobres de obligacion + escalera (CFO Autonomo, /business/sobres) =====
// v1: disena, simula y propone — NINGUNO de estos endpoints mueve dinero.

export async function sobresGet() {
  const { data } = await CEREBRO_API.get('/api/business-sobres', { timeout: 15000 });
  return data.data;
}

export async function sobresSet(sobres) {
  const { data } = await CEREBRO_API.post('/api/business-sobres', { sobres }, { timeout: 15000 });
  return data.data;
}

export async function sobresSimular(monto, moneda = 'COP') {
  const { data } = await CEREBRO_API.post('/api/business-sobres/simular', { monto, moneda }, { timeout: 15000 });
  return data.data;
}

// Activacion de Rendimientos Business (Rewards empresa: preview + SMS + on-chain)
export async function rendimientosPosicion() {
  const { data } = await CEREBRO_API.get('/api/business-rendimientos/posicion', { timeout: 15000 });
  return data?.data;
}
export async function rendimientosPreparar(montoUsd) {
  const { data } = await CEREBRO_API.post('/api/business-rendimientos/preparar', { montoUsd }, { timeout: 15000 });
  return data?.data;
}
export async function rendimientosSendCode() {
  const { data } = await CEREBRO_API.post('/api/business-rendimientos/send-code', {}, { timeout: 15000 });
  return data?.data;
}
export async function rendimientosActivar(token, codigo) {
  // Los fallos de dinero llegan como 422: devolvemos el data igual para mostrar el motivo.
  try {
    const { data } = await CEREBRO_API.post('/api/business-rendimientos/activar', { token, codigo }, { timeout: 120000 });
    return data?.data;
  } catch (e) {
    if (e?.response?.status === 422) return e.response.data?.data;
    throw e;
  }
}
export async function rendimientosRetirar(rewardId, codigo) {
  try {
    const { data } = await CEREBRO_API.post('/api/business-rendimientos/retirar', { rewardId, codigo }, { timeout: 120000 });
    return data?.data;
  } catch (e) {
    if (e?.response?.status === 422) return e.response.data?.data;
    throw e;
  }
}

export async function escaleraGet() {
  const { data } = await CEREBRO_API.get('/api/business-sobres/escalera', { timeout: 20000 });
  return data.data;
}

export async function oraculoChat(message, history = []) {
  try {
    const { data } = await CEREBRO_API.post('/api/oraculo/chat', { message, history }, { timeout: 30000 });
    return data.data || { response: 'Sin respuesta del Oraculo.', toolsUsed: [] };
  } catch (err) {
    console.error('[Oraculo] chat error:', err?.response?.data?.message || err.message);
    if (err?.response?.status === 429) {
      return { response: 'Has enviado muchos mensajes. Espera un momento antes de continuar.', toolsUsed: [] };
    }
    return { response: 'No pude conectarme con el Oraculo. Verifica tu conexion e intenta de nuevo.', toolsUsed: [] };
  }
}

/**
 * Envia codigo SMS para verificar un trade del Oraculo.
 */
export async function oraculoSendCode(phone) {
  try {
    // El backend responde demo:true cuando la cuenta esta en la allowlist de
    // pruebas: dice "Codigo enviado" pero NO manda SMS, hay que usar el codigo
    // fijo. Si no se propaga esa marca, la pantalla deja al usuario esperando
    // un mensaje que nunca va a llegar.
    const { data } = await CEREBRO_API.post('/api/oraculo/send-code', { phone }, { timeout: 15000 });
    return { success: true, demo: !!data?.demo };
  } catch (err) {
    console.error('[Oraculo] sendCode error:', err?.response?.data?.message || err.message);
    return { success: false, error: asText(err?.response?.data?.message, 'Error enviando codigo') };
  }
}

/**
 * Verifica codigo SMS y marca trade como listo para ejecutar.
 */
export async function oraculoVerifyTrade(tradeToken, smsCode, phone) {
  try {
    const { data } = await CEREBRO_API.post('/api/oraculo/verify-trade', { tradeToken, smsCode, phone }, { timeout: 15000 });
    return { success: true, data };
  } catch (err) {
    console.error('[Oraculo] verifyTrade error:', err?.response?.data?.message || err.message);
    return { success: false, error: asText(err?.response?.data?.message, 'Codigo incorrecto') };
  }
}

/**
 * Ejecuta directamente un trade verificado (deterministico, sin Claude).
 */
export async function oraculoExecuteTrade(tradeToken) {
  try {
    const { data } = await CEREBRO_API.post('/api/oraculo/execute', { tradeToken }, { timeout: 30000 });
    return { success: true, data: data.data };
  } catch (err) {
    console.error('[Oraculo] executeTrade error:', err?.response?.data?.message || err.message);
    return { success: false, error: asText(err?.response?.data?.message, 'Error ejecutando') };
  }
}

// Coerciona cualquier error de API a TEXTO. Un backend puede responder message como
// OBJETO (ej Bridge {message:{code,message}}); si ese objeto llega a un render de React
// tumba la app entera (error #31 — incidente pantalla blanca 20-jul).
function asText(m, fallback) {
  if (typeof m === 'string' && m) return m;
  if (m && typeof m.message === 'string') return m.message;
  if (m != null && typeof m === 'object') { try { return JSON.stringify(m); } catch { /* circular */ } }
  return fallback;
}

// F-E: extrae datos de una cuenta de EE.UU. desde una FOTO (data URL). Vision de un tiro.
export async function oraculoExtractAccountPhoto(image) {
  try {
    const { data } = await CEREBRO_API.post('/api/oraculo/extract-account-photo', { image }, { timeout: 45000 });
    return { success: true, data: data.data };
  } catch (err) {
    console.error('[Oraculo] extractAccountPhoto error:', err?.response?.data?.message || err.message);
    return { success: false, error: asText(err?.response?.data?.message, 'No pude leer la foto') };
  }
}

// F-E: prepara el registro de una cuenta de EE.UU. con los campos ya revisados. Devuelve tradeToken.
export async function oraculoPrepareIntlAccount(fields) {
  try {
    const { data } = await CEREBRO_API.post('/api/oraculo/prepare-intl-account', fields, { timeout: 15000 });
    return { success: true, data: data.data };
  } catch (err) {
    console.error('[Oraculo] prepareIntlAccount error:', err?.response?.data?.message || err.message);
    return { success: false, error: asText(err?.response?.data?.message, 'No se pudo preparar el registro') };
  }
}

// Reasignacion entre metas — un toque, sin SMS (no mueve dinero de la cuenta).
export async function oraculoExecuteReallocation(reallocToken) {
  try {
    const { data } = await CEREBRO_API.post('/api/oraculo/execute-reallocation', { reallocToken }, { timeout: 20000 });
    return { success: true, data: data.data };
  } catch (err) {
    console.error('[Oraculo] reallocation error:', err?.response?.data?.message || err.message);
    return { success: false, error: err?.response?.data?.message || 'No se pudo reasignar' };
  }
}

// ── Ordenes condicionales (sinteticas): el Oraculo las crea, la app las muestra ──

export async function getConditionalOrders() {
  try {
    const { data } = await CEREBRO_API.get('/api/orders/conditional', { timeout: 12000 });
    return data?.data || [];
  } catch (err) {
    console.error('[Condicionales] list error:', err?.response?.data?.message || err.message);
    return [];
  }
}

export async function cancelConditionalOrder(id) {
  try {
    const { data } = await CEREBRO_API.delete(`/api/orders/conditional/${id}`, { timeout: 12000 });
    return { success: !!data?.success };
  } catch (err) {
    console.error('[Condicionales] cancel error:', err?.response?.data?.message || err.message);
    return { success: false, error: err?.response?.data?.message || 'No se pudo cancelar' };
  }
}

// ── Multimoneda (SPEC-portafolio-multimoneda F-B) ──

export async function getTrm() {
  try {
    const { data } = await CEREBRO_API.get('/api/market/trm', { timeout: 10000 });
    return Number(data?.data?.rate || 0);
  } catch { return 0; }
}

export async function getFondosProgresion() {
  try {
    const { data } = await CEREBRO_API.get('/api/market/fondos-progresion', { timeout: 12000 });
    return data?.data || [];
  } catch { return []; }
}

export default CEREBRO_API;

// --- Salud de caja + Calendario de obligaciones (CFO Autonomo) — investments-api /api/business-caja ---
export async function getCaja() {
  const { data } = await CEREBRO_API.get('/api/business-caja', { timeout: 8000 });
  return data?.data; // { empresa, nit, moneda, ivaPeriodicidad, saldoManual, obligaciones } | { sinEmpresa }
}
export async function saveCaja(obligaciones, saldoManual, ivaPeriodicidad) {
  const { data } = await CEREBRO_API.post('/api/business-caja', { obligaciones, saldoManual, ivaPeriodicidad }, { timeout: 8000 });
  return data?.data; // { ok, ivaPeriodicidad, obligaciones }
}
export async function saludCaja(saldo, ingresosEsperados) {
  const { data } = await CEREBRO_API.post('/api/business-caja/salud', { saldo, ingresosEsperados }, { timeout: 8000 });
  return data?.data; // { ok, semaforo, descubierto, detalle, resumenTexto, ... } | { ok:false, mensaje }
}
export async function sugerirFechasCaja(ivaPeriodicidad) {
  const { data } = await CEREBRO_API.post('/api/business-caja/sugerir-fechas', { ivaPeriodicidad }, { timeout: 8000 });
  return data?.data; // { ok, ivaPeriodicidad, obligaciones }
}
